import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import { useOutletContext } from 'react-router-dom-v5-compat';
import { compose } from 'redux';

import type { FormJobOutletContext } from 'af-root/scenes/Company/Jobs/Form/types';

import { isAllowed } from '@acceligentllc/shared/utils/auth';

import type { RootState } from 'af-reducers';

import PagePermissions from 'ab-enums/pagePermissions.enum';

import * as accountActions from 'af-actions/accounts';
import * as divisionActions from 'af-actions/division';
import * as employeeActions from 'af-actions/employee';
import * as jobStatusActions from 'af-actions/jobStatus';
import * as locationActions from 'af-actions/location';
import * as saleActions from 'af-actions/sale';

import styles from '../styles.module.scss';
import ContactsSection from './Sections/Contacts';
import CustomerSignature from './Sections/CustomerSignatureSection';
import DeliverableSection from './Sections/DeliverableSection';
import DisposalSection from './Sections/DisposalSection';
import JobDetailSection from './Sections/JobDetailSection';
import JobSummarySection from './Sections/JobSummarySection';
import PrevailingWageDeductionSection from './Sections/PrevailingWageDeductionSection';
import ProjectSection from './Sections/ProjectSection';
import RequierementsSection from './Sections/RequierementsSection';
import SalesSection from './Sections/SalesSection';
import ScheduleInfo from './Sections/ScheduleInfo';
import SummaryOfWorkSection from './Sections/SummaryOfWorkSection';
import SurveyDataSection from './Sections/SurveyDataSection';
import TrucksAndEquipmentSection from './Sections/TrucksAndEquipment';
import WorkLocationSection from './Sections/WorkLocation';

type Props = ConnectedProps<typeof connector>;

const DetailsFormTab: React.FC<Props> = (props) => {
	const {
		findSales,
		findJobStatuses,
		findAllProjectManagers,
		findAllSisAndPMsWithEmails,
		findAllLocations,
		findAllDivisions,
		findAllSuperintendentsAndProjectManagers,
		hasContactPermission,
		onlyDeliverableAllowed,
	} = props;

	const salesRef = React.useRef<HTMLDivElement>(null);

	const {
		isProject,
		projectId,
		redirectOnSubmit,
		orgAlias,
		subjobs,
		availableSubjobs,
		onTurnToProjectChange,
		handleSubjobSelect,
		setSubjobSearchText,
		goToJobForm,
		selectedSubjob,
		openAddSubjobConfirmationModal,
		turnToProjectDisabled,
		disableTurnToProject,
		subjobSearchText,
		change,
		workRequestId,
		formName,
		scrollToTopOfFormForSubjobRedirect,
		workLocation,
		invoiceReminderRecipients,
	} = useOutletContext<FormJobOutletContext>();

	React.useEffect(() => {
		if (salesRef.current) {
			salesRef.current.scrollIntoView({ block: 'end' });
		}
	}, []);

	React.useEffect(() => {
		if (salesRef.current && scrollToTopOfFormForSubjobRedirect) {
			salesRef.current.scrollIntoView({ block: 'end' });
		}
	}, [scrollToTopOfFormForSubjobRedirect]);

	const clearAddress = React.useCallback(() => {
		change('workLocation', null);
	}, [change]);

	return (
		<div className={styles['job-form__tab-content']} >
			<SalesSection findSales={findSales} isDisabled={onlyDeliverableAllowed} salesRef={salesRef} />
			<JobSummarySection
				change={change}
				findAllDivisions={findAllDivisions}
				findAllLocations={findAllLocations}
				findAllProjectManagers={findAllProjectManagers}
				findAllSisAndPMs={findAllSisAndPMsWithEmails}
				findJobStatuses={findJobStatuses}
				invoiceReminderRecipients={invoiceReminderRecipients}
				isDisabled={onlyDeliverableAllowed}
				workRequestId={workRequestId!}
			/>
			<JobDetailSection isDisabled={onlyDeliverableAllowed} />
			{
				process.env.FTD_DISABLE_PR_DIFF !== 'true' &&
				<PrevailingWageDeductionSection change={change} formName={formName} isDisabled={onlyDeliverableAllowed} />
			}
			<RequierementsSection isDisabled={onlyDeliverableAllowed} />
			<ScheduleInfo change={change} formName={formName} isDisabled={onlyDeliverableAllowed} />
			{hasContactPermission && <ContactsSection formName={formName} isDisabled={onlyDeliverableAllowed} />}
			<DeliverableSection change={change} formName={formName} />
			<WorkLocationSection
				clearAddress={clearAddress}
				formName={formName}
				isDisabled={onlyDeliverableAllowed}
				workLocation={workLocation}
			/>
			<SummaryOfWorkSection isDisabled={onlyDeliverableAllowed} />
			<SurveyDataSection findAllSuperintendentsAndProjectManagers={findAllSuperintendentsAndProjectManagers} isDisabled={onlyDeliverableAllowed} />
			<TrucksAndEquipmentSection isDisabled={onlyDeliverableAllowed} />
			<DisposalSection change={change} formName={formName} isDisabled={onlyDeliverableAllowed} />
			<ProjectSection
				availableSubjobs={availableSubjobs}
				disableTurnToProject={disableTurnToProject}
				formName={formName}
				goToJobForm={goToJobForm}
				handleSubjobSelect={handleSubjobSelect}
				isProject={isProject}
				onTurnToProjectChange={onTurnToProjectChange}
				openAddSubjobConfirmationModal={openAddSubjobConfirmationModal}
				orgAlias={orgAlias}
				projectId={projectId}
				redirectOnSubmit={redirectOnSubmit}
				selectedSubjob={selectedSubjob}
				setSubjobSearchText={setSubjobSearchText}
				subjobs={subjobs}
				subjobSearchText={subjobSearchText}
				turnToProjectDisabled={turnToProjectDisabled}
				workRequestId={workRequestId}
			/>
			<CustomerSignature isDisabled={onlyDeliverableAllowed} />
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { user: { companyData, userData } } = state;
	if (!userData || !companyData) {
		throw new Error('User not logged in');
	}

	const hasCreatePermissions = isAllowed(PagePermissions.COMPANY.JOBS.CREATE, companyData.permissions, companyData.isCompanyAdmin, userData.role);
	const hasEditPermissions = isAllowed(PagePermissions.COMPANY.JOBS.EDIT, companyData.permissions, companyData.isCompanyAdmin, userData.role);
	const hasContactPermission = isAllowed(PagePermissions.COMPANY.CONTACTS, companyData.permissions, companyData.isCompanyAdmin, userData.role);

	return {
		onlyDeliverableAllowed: hasEditPermissions && !hasCreatePermissions,
		hasContactPermission,
	};
}

function mapDispatchToProps() {

	return {
		findSales: saleActions.findAllForForm,
		findJobStatuses: jobStatusActions.findList,
		findAllProjectManagers: employeeActions.findAllProjectManagers,
		findAllSisAndPMsWithEmails: accountActions.findSuperintendentsAndProjectManagersWithEmail,
		findAllLocations: locationActions.findList,
		findAllDivisions: divisionActions.findAllForCompany,
		findAllSuperintendentsAndProjectManagers: employeeActions.findAllSuperintendentsAndProjectManagers,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentType>(
	React.memo,
	connector
);

export default enhance(DetailsFormTab);
